import Button from "app/components/button/button";
import SocialIcons from "app/components/ido-details/social-icons/social-icons";
import { FC } from "react";
import { useHistory } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import useWidth from "app/hooks/useWidth";
import { useTheme } from "app/shared";
import styles from "./IdoTracker.module.scss";

interface IDOCard {
  title: string;
  description: string;
  status: string;
  socialLinks: any;
  cta: string;
  _id: string;
}

interface IDOTrackerProps {
  pastIDOs?: { list: any[]; meta: any } | undefined;
  whitelistedIDOs?: { list: any[]; meta: any } | undefined;
  newIDOs?: { list: any[]; meta: any } | undefined;
}

const IDOTracker: FC<IDOTrackerProps> = ({
  pastIDOs,
  whitelistedIDOs,
  newIDOs,
}) => {
  const history = useHistory();
  const { isMobile } = useWidth();
  const { theme } = useTheme();

  const handleClick = (_id: string) => {
    history.push(`/ido/${_id}`);
  };

  const renderIDOSection = (title: string, idos: any[]) => {
    return (
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        {idos.length > 1 ? (
          <Swiper
            className={styles.swiper}
            modules={[Navigation, Pagination]}
            spaceBetween={16}
            slidesPerView={
              !isMobile ? Math.min(2, idos.length) : Math.min(1, idos.length)
            }
            pagination={{ clickable: true }}
          >
            {idos.map((ido, index) => (
              <SwiperSlide key={index}>
                <div
                  className={`${styles.card} ${
                    theme === "dark" ? styles.darkTheme : ""
                  }`}
                >
                  <h3 className={styles.cardTitle}>{ido.title}</h3>
                  <p
                    className={`${styles.cardDescription} ${
                      theme === "dark" ? styles.darkTheme : ""
                    }`}
                  >
                    {ido.description}
                  </p>
                  {ido.status === "Past" && (
                    <span className={`${styles.tag} ${styles.idoEnded}`}>
                      IDO Sold Out
                    </span>
                  )}
                  {ido.status === "Upcoming" && (
                    <span className={`${styles.tag} ${styles.whitelistOpen}`}>
                      SmartWhitelist Soon
                    </span>
                  )}
                  {ido.status === "Whitelisted" && (
                    <span className={`${styles.tag} ${styles.whitelistOpen}`}>
                      SmartWhitelist
                    </span>
                  )}
                  <SocialIcons isDashboard links={ido.socialLinks} />
                  <Button onClick={() => handleClick(ido._id)}>
                    Check Details
                  </Button>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className={styles.cardsContainer}>
            {idos.map((ido, index) => (
              <div
                key={index}
                className={`${styles.card} ${
                  theme === "dark" ? styles.darkTheme : ""
                }`}
              >
                <h3 className={styles.cardTitle}>{ido.title}</h3>
                <p
                  className={`${styles.cardDescription} ${
                    theme === "dark" ? styles.darkTheme : ""
                  }`}
                >
                  {ido.description}
                </p>
                {ido.status === "Past" && (
                  <span className={`${styles.tag} ${styles.idoEnded}`}>
                    IDO Sold Out
                  </span>
                )}
                {ido.status === "Upcoming" && (
                  <span className={`${styles.tag} ${styles.whitelistOpen}`}>
                    SmartWhitelist Soon
                  </span>
                )}
                {ido.status === "Whitelisted" && (
                  <span className={`${styles.tag} ${styles.whitelistOpen}`}>
                    SmartWhitelist
                  </span>
                )}
                <SocialIcons isDashboard links={ido.socialLinks} />
                <Button onClick={() => handleClick(ido._id)}>
                  Check Details
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`${styles.container} ${
        theme === "dark" ? styles.darkTheme : ""
      }`}
    >
      <h3 className={styles.title}>My IDO Tracker</h3>
      {!(
        pastIDOs?.list?.length ||
        whitelistedIDOs?.list?.length ||
        newIDOs?.list?.length
      ) && (
        <div className={styles.noData}>
          You haven't participated in any IDOs yet. This section will display
          data about your IDO activities.
        </div>
      )}
      {pastIDOs?.list &&
        pastIDOs.list.length > 0 &&
        renderIDOSection("📈 Past IDOs:", pastIDOs.list)}
      {whitelistedIDOs?.list &&
        whitelistedIDOs.list.length > 0 &&
        renderIDOSection("✅ Whitelisted IDOs:", whitelistedIDOs.list)}
      {newIDOs?.list &&
        newIDOs.list.length > 0 &&
        renderIDOSection("🚀 New IDOs:", newIDOs.list)}
    </div>
  );
};

export default IDOTracker;
